// Trip scheduler service.
//
// Persists an angler's planned future trips to Firestore
// (users/{uid}/scheduledTrips/{id}) and schedules a device notification via
// @capacitor/local-notifications that fires at the start time to let them know
// their planned session has started.
//
// The notification layer is native-only: on the web/PWA build the plugin is a
// no-op and the app relies on the in-app "Planned Trips" card instead. All
// notification calls are guarded and never throw into the UI.

import { Capacitor } from "@capacitor/core";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { authedWrite } from "../utils/authedWrite";

const IS_NATIVE = (() => {
  try {
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
})();

// Lazily loaded so the web bundle never touches the native module at import.
let _plugin = null;      // Capacitor plugin proxy, once loaded
let _pluginLoad = null;  // in-flight import promise (dedupes concurrent callers)

// Ensure the native plugin is loaded. Returns TRUE when it's available.
//
// CRITICAL: never `return` the Capacitor plugin proxy from an async function.
// Resolving a promise with the proxy makes the runtime thenable-check it by
// accessing `proxy.then`, which Capacitor treats as a native method call and
// rejects with "LocalNotifications.then() is not implemented on android". That
// rejection was silently breaking scheduleReminder AND cancelReminder — and
// since deleteScheduledTrip awaits cancelReminder before deleteDoc, deletes
// never reached the delete. Callers read the module-level `_plugin` directly.
async function ensurePlugin() {
  if (!IS_NATIVE) return false;
  if (_plugin) return true;
  if (!_pluginLoad) {
    _pluginLoad = import("@capacitor/local-notifications")
      .then((mod) => { _plugin = mod.LocalNotifications; })
      .catch(() => { _plugin = null; });
  }
  await _pluginLoad;
  return !!_plugin;
}

// Local-notification ids must be 32-bit ints. Generate a stable positive id.
function genNotificationId() {
  return Math.floor(Math.random() * 2_000_000_000) + 1;
}

const tripsCol = (uid) => collection(db, "users", uid, "scheduledTrips");

export const REPEAT_OPTIONS = ["once", "daily", "weekly"];

/**
 * Subscribe to a user's planned trips, ordered by start time.
 * @returns unsubscribe function
 */
export function watchScheduledTrips(uid, onData) {
  if (!uid) {
    onData([]);
    return () => {};
  }
  const q = query(tripsCol(uid), orderBy("startAt", "asc"));
  return onSnapshot(
    q,
    (snap) => {
      // Mirror server-side cleanup: when a trip doc disappears (tournament
      // cancelled, team removed, or deleted from another device) cancel its
      // local reminders here too. cancelReminder clears both the T-1h id and
      // the T-0 (id + 1) prompt, and is a no-op for ids that aren't scheduled.
      snap.docChanges().forEach((chg) => {
        if (chg.type === "removed") {
          const nid = chg.doc.data()?.notificationId;
          if (nid) cancelReminder(nid);
        }
      });
      onData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.warn("[tripScheduler] watch failed:", err?.message || err);
      onData([]);
    }
  );
}

/**
 * Re-arm local reminders for server-created trips (e.g. tournament auto-enroll)
 * that this device never scheduled itself at save time. Safe to call on every
 * load / foreground: scheduleReminder is idempotent by notificationId, so
 * re-running just replaces the existing two notifications rather than
 * duplicating them.
 *
 * Only trips with source === "tournament-auto" are touched — app-made trips are
 * already scheduled when saved via saveScheduledTrip.
 * @param {Array<object>} trips - docs as delivered by watchScheduledTrips
 */
export async function reconcileTripReminders(trips) {
  if (!IS_NATIVE || !Array.isArray(trips) || !trips.length) return;
  for (const trip of trips) {
    if (trip?.source !== "tournament-auto") continue; // app-made trips already scheduled
    if (!trip.notificationId) continue;
    if (trip.autoStartedAt) continue; // already started
    const startMs = trip.startAt?.toMillis?.() ?? trip.startAtMs;
    const recurring = trip.repeat && trip.repeat !== "once";
    if (!recurring && (!startMs || startMs <= Date.now())) continue; // past/invalid one-off
    // scheduleReminder reads startAt (Timestamp) directly and keys off
    // notificationId, so passing the raw doc re-registers the same ids.
    // eslint-disable-next-line no-await-in-loop
    await scheduleReminder(trip);
  }
}

/** Ask for notification permission (native only). Returns true if granted. */
export async function ensureNotificationPermission() {
  if (!(await ensurePlugin())) return false;
  const LN = _plugin;
  try {
    const chk = await LN.checkPermissions();
    if (chk.display === "granted") return true;
    const req = await LN.requestPermissions();
    return req.display === "granted";
  } catch (err) {
    console.warn("[tripScheduler] permission request failed:", err?.message || err);
    return false;
  }
}

const REMINDER_LEAD_MS = 60 * 60 * 1000; // "starts soon" reminder fires 1h before

function startedBody(trip) {
  const where = trip.location?.label ? ` at ${trip.location.label}` : "";
  return `It's time to fish${where}. Tap Start to begin your session.`;
}

function reminderBody(trip) {
  const where = trip.location?.label ? ` at ${trip.location.label}` : "";
  return `Your session${where} starts in about an hour. Get your gear ready!`;
}

// The T-0 notification carries a "Start session" action button (native). When
// tapped, App.jsx auto-starts the session (see localNotificationActionPerformed).
const SCHEDULED_TRIP_ACTION_TYPE = "SCHEDULED_TRIP";
const START_SESSION_ACTION_ID = "START_SESSION";
let _actionTypesRegistered = false;
async function registerTripActionTypes(LN) {
  if (_actionTypesRegistered) return;
  try {
    await LN.registerActionTypes({
      types: [
        {
          id: SCHEDULED_TRIP_ACTION_TYPE,
          actions: [{ id: START_SESSION_ACTION_ID, title: "Start session", foreground: true }],
        },
      ],
    });
    _actionTypesRegistered = true;
  } catch {
    /* older plugin / web build — tapping the body still opens the app */
  }
}

// Second notification id (the T-0 start prompt) derived from the trip's reminder
// id. genNotificationId caps below 2^31, so +1 stays a valid 32-bit int.
const startNotificationId = (reminderId) => reminderId + 1;

async function scheduleReminder(trip) {
  if (!(await ensurePlugin())) return;
  const LN = _plugin;
  const startMs = trip.startAt?.toMillis ? trip.startAt.toMillis() : trip.startAtMs;
  if (!startMs) return;
  await registerTripActionTypes(LN);

  const reminderId = trip.notificationId;
  const startId = startNotificationId(reminderId);

  // Always clear any prior schedule for both ids first.
  try {
    await LN.cancel({ notifications: [{ id: reminderId }, { id: startId }] });
  } catch {
    /* nothing scheduled yet */
  }

  const repeats = trip.repeat && trip.repeat !== "once";
  const every = trip.repeat === "weekly" ? "week" : "day";
  const mkSchedule = (ms) =>
    repeats
      ? { at: new Date(ms), repeats: true, every, allowWhileIdle: true }
      : { at: new Date(ms), allowWhileIdle: true };

  const notifications = [];

  // T-1h "starts soon" reminder (skip if a one-off is already within the hour).
  const reminderMs = startMs - REMINDER_LEAD_MS;
  if (repeats || reminderMs > Date.now()) {
    notifications.push({
      id: reminderId,
      title: trip.name ? `${trip.name} — starting soon` : "Your session starts soon",
      body: reminderBody(trip),
      schedule: mkSchedule(reminderMs),
      smallIcon: "ic_stat_icon_config_sample",
      extra: { tripId: trip.id, type: "scheduledTripReminder" },
    });
  }

  // T-0 "time to fish" prompt with a Start-session action button.
  if (repeats || startMs > Date.now()) {
    notifications.push({
      id: startId,
      title: trip.name ? `${trip.name} — time to fish` : "Time to start your session",
      body: startedBody(trip),
      schedule: mkSchedule(startMs),
      smallIcon: "ic_stat_icon_config_sample",
      actionTypeId: SCHEDULED_TRIP_ACTION_TYPE,
      extra: { tripId: trip.id, type: "scheduledTripStart" },
    });
  }

  if (!notifications.length) return;
  try {
    await LN.schedule({ notifications });
  } catch (err) {
    console.warn("[tripScheduler] schedule failed:", err?.message || err);
  }
}

async function cancelReminder(notificationId) {
  if (!notificationId || !(await ensurePlugin())) return;
  const LN = _plugin;
  try {
    await LN.cancel({
      notifications: [{ id: notificationId }, { id: startNotificationId(notificationId) }],
    });
  } catch {
    /* already gone */
  }
}

/**
 * Create or update a planned trip and (re)schedule its start notification.
 * @param {string} uid
 * @param {object} input - { id?, name, startAtMs, location, targetSpeciesIds,
 *   notes, repeat, smartSuggested, notificationId? }
 * @returns the saved trip { id, ...data }
 */
export async function saveScheduledTrip(uid, input) {
  if (!uid) throw new Error("Not signed in");
  if (!input?.startAtMs) throw new Error("A start date & time is required");

  const id = input.id || doc(tripsCol(uid)).id;
  const notificationId = input.notificationId || genNotificationId();

  const data = {
    name: (input.name || "").trim(),
    startAt: Timestamp.fromMillis(input.startAtMs),
    location: input.location || null,
    targetSpeciesIds: Array.isArray(input.targetSpeciesIds) ? input.targetSpeciesIds : [],
    notes: (input.notes || "").trim(),
    repeat: REPEAT_OPTIONS.includes(input.repeat) ? input.repeat : "once",
    smartSuggested: !!input.smartSuggested,
    notificationId,
    updatedAt: serverTimestamp(),
    ...(input.id ? {} : { createdAt: serverTimestamp() }),
  };

  await authedWrite(() => setDoc(doc(tripsCol(uid), id), data, { merge: true }), "saveScheduledTrip");

  // Fire-and-forget the native reminder; a scheduling hiccup shouldn't fail
  // the save (the trip is still persisted and shown in-app).
  scheduleReminder({ id, ...data, startAtMs: input.startAtMs });

  return { id, ...data };
}

/** Mark a one-off trip as auto-started so it never triggers auto-start again
 * (survives dashboard remounts and app restarts). */
export async function markTripAutoStarted(uid, tripId) {
  if (!uid || !tripId) return;
  try {
    await authedWrite(
      () => setDoc(doc(tripsCol(uid), tripId), { autoStartedAt: serverTimestamp() }, { merge: true }),
      "markTripAutoStarted"
    );
  } catch (e) {
    console.warn("[tripScheduler] markTripAutoStarted failed:", e?.message || e);
  }
}

/** Delete a planned trip and cancel its reminder. */
export async function deleteScheduledTrip(uid, trip) {
  if (!uid || !trip?.id) return;
  // Cancelling the native reminder is best-effort — it must never block the
  // Firestore delete (a plugin hiccup would otherwise strand the trip doc).
  try {
    await cancelReminder(trip.notificationId);
  } catch {
    /* non-fatal */
  }
  await authedWrite(() => deleteDoc(doc(tripsCol(uid), trip.id)), "deleteScheduledTrip");
}
